import { JSXElementConstructor, useCallback, useMemo } from 'react';

import { CellContext, RowData } from '@tanstack/react-table';
import { ActionMenu, ActionMenuContext, ActionMenuProps } from 'ui/Core';

import { useContextMenu } from '../../../TableContent/TableBody/row/useContextMenu';
import { ActionColumnProps } from '../../../types/ActionColumn';
import { RowActionTarget } from '../RowActionTarget/RowActionTarget';
import { RowActionMenuProvider, useRowActionMenu } from './RowAction.context';

type RowActionProps<TData extends RowData> = {
  Actions: JSXElementConstructor<ActionColumnProps<TData>>;
} & ActionMenuProps;

const RowAction = <TData extends RowData>({ Actions, ...rest }: RowActionProps<TData>) => {
  const { cellContext } = useRowActionMenu();
  return <Actions {...cellContext} {...rest} />;
};

const RowActionMenu = <TData extends RowData>({
  Actions,
  cellContext,
}: {
  Actions: JSXElementConstructor<ActionColumnProps<TData>>;
  cellContext: CellContext<TData, unknown>;
}) => {
  const { info, isOpenedViaContextMenu, setIsOpenedViaContextMenu } = useContextMenu();

  const dropdownProps = useMemo(
    () => (isOpenedViaContextMenu ? { top: info.y, left: info.x } : {}),
    [info.x, info.y, isOpenedViaContextMenu]
  );

  const menuProps = useMemo(
    () => ({
      onExitTransitionEnd() {
        setIsOpenedViaContextMenu(false);
      },
    }),
    [setIsOpenedViaContextMenu]
  );

  return (
    <RowActionMenuProvider value={{ cellContext }}>
      <ActionMenu
        Target={RowActionTarget}
        // Note: Call back is needed here; this is generally a weird pattern to use a callback to render a component
        // But in this case, we need to pass the Actions component to the RowAction component
        // and Actions component is a JSXElementConstructor, so we need to use a callback to render it
        Actions={useCallback(
          (args: ActionMenuContext) => (
            <RowAction Actions={Actions} {...args} />
          ),
          [Actions]
        )}
        menuProps={menuProps}
        dropdownProps={dropdownProps}
      />
    </RowActionMenuProvider>
  );
};

export { RowActionMenu };
